import { useCallback, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from "react";
import type { Annotation, AnnotationColor, AnnotationId, EncounterCommand } from "@lancer-vtt/domain";
import type { EncounterDispatchResult } from "../../../lib/encounter/remoteEncounterController";
import { annotationsAlongSegment, eraseAnnotationsCommand, freehandAnnotationCommand, polylineAnnotationCommand, type DrawPoint } from "../combatDrawing";

export type DrawGestures = {
  /** The latest `drawColor`/`actorId`/`dispatch`, mirrored into a ref so the window-level nets and
   *  the finishers always read the current values. */
  drawCtx: RefObject<{ drawColor: AnnotationColor; actorId: string; dispatch: (command: EncounterCommand) => EncounterDispatchResult }>;
  freehandRef: RefObject<DrawPoint[] | null>;
  freehandPreview: DrawPoint[] | null;
  setFreehandPreview: Dispatch<SetStateAction<DrawPoint[] | null>>;
  finishFreehand: () => void;
  polylineRef: RefObject<DrawPoint[] | null>;
  polylinePreview: DrawPoint[] | null;
  setPolylinePreview: Dispatch<SetStateAction<DrawPoint[] | null>>;
  polylineCursor: DrawPoint | null;
  setPolylineCursor: Dispatch<SetStateAction<DrawPoint | null>>;
  polylineRawClicksRef: RefObject<[DrawPoint, DrawPoint] | null>;
  finishPolyline: () => void;
  /** Drops the half-drawn polyline without committing (Esc). */
  dropPolyline: () => void;
  eraserCtx: RefObject<{ isGm: boolean; actorId: string; annotations: Record<AnnotationId, Annotation>; dispatch: (command: EncounterCommand) => EncounterDispatchResult }>;
  isErasable: (id: AnnotationId) => boolean;
  eraserRef: RefObject<{ lastPoint: DrawPoint; ids: Set<AnnotationId> } | null>;
  /** Open an eraser drag at `world`, erasing whatever is under the press itself. */
  openEraser: (world: DrawPoint) => void;
  /** Extend the open eraser drag to `world`, collecting every erasable stroke the travel touched. */
  sampleEraser: (world: DrawPoint) => void;
  /** Commit the open eraser drag as one `batch` (one undo). */
  finishEraser: () => void;
  /** Abandon the open eraser drag: nothing is sent, and the hidden strokes come back. */
  dropEraser: () => void;
  /** The strokes the open drag has collected, hidden from the board until it commits or is dropped. */
  erasingIds: ReadonlySet<AnnotationId>;
};

const NONE: ReadonlySet<AnnotationId> = new Set();

/** `annotations` without the strokes an open eraser drag has collected: what the board draws mid-drag. */
export function visibleAnnotations(annotations: Record<AnnotationId, Annotation>, erasing: ReadonlySet<AnnotationId>): Record<AnnotationId, Annotation> {
  if (erasing.size === 0) return annotations;
  return Object.fromEntries(Object.entries(annotations).filter(([id]) => !erasing.has(id)));
}

/**
 * ── Draw annotations + the eraser ──
 *
 * Mode-independent — armed via `drawTool` regardless of GM/player or EDIT/PLAY (the host,
 * `CombatApp`, keeps it mutually exclusive with the terrain rail's brush/tool; see the prop
 * doc). Two gestures, mirroring the `strokeRef`/`roomRef` pattern in {@link useAuthoringGestures}
 * but building an `add_annotation` command through the pure `combatDrawing` module instead of a
 * `batch`:
 *   - **Freehand** is a DRAG: `onMouseDown` opens `freehandRef`, `onMouseMove` distance-samples
 *     into it (`shouldSampleFreehandPoint`), and release RDP-simplifies + commits ONE
 *     `add_annotation` ({@link DrawGestures.finishFreehand}). Like every other drag gesture here, Esc
 *     ABANDONS it (no commit) — the stage's `cancelGesture` covers it alongside the tile/wall/room drags.
 *   - **Polyline** is a CLICK-ACCUMULATE: each `onMouseDown` appends a vertex
 *     (`accumulatePolylineVertex`, which dedupes the finishing double-click's repeat
 *     point), and double-click / Enter FINISH it (commit the accumulated vertices verbatim).
 *     Esc DROPS it ({@link DrawGestures.dropPolyline}, `decisions/0189` point 7) and Draw stays armed.
 *
 * The ERASER is a THIRD `DrawTool`, not a new rail (D5) — it inherits the host's mutual disarm, the
 * layer-3 Esc entry, the authoring cursor class, and Draw's first claim on the left button for free. A DRAG,
 * mirroring freehand's own shape exactly: `onMouseDown` opens `eraserRef`, each `onMouseMove`
 * hit-tests the segment TRAVELED since the last sample against every persisted stroke (**D6**,
 * `annotationsAlongSegment` — the segment-vs-segment test, not a point test, so a fast drag can't
 * skip straight through a stroke between two samples) and accumulates every ERASABLE id it touches
 * into a `Set` (**D7** — filtered live via {@link DrawGestures.isErasable}: GM erases anything, a
 * player only their own), and release commits ONE `batch` (**D8**, `eraseAnnotationsCommand`) — so
 * the whole drag undoes as a single unit. The collected strokes vanish from this viewer's board as
 * the eraser passes ({@link DrawGestures.erasingIds}, hidden locally, not sent), so the drag reads as
 * erasing along the way; other viewers see the result when the batch lands on release.
 */
export function useDrawGestures({
  drawColor,
  actorId,
  dispatch,
  isGm,
  annotations,
}: {
  drawColor: AnnotationColor;
  actorId: string;
  dispatch: (command: EncounterCommand) => EncounterDispatchResult;
  isGm: boolean;
  annotations: Record<AnnotationId, Annotation>;
}): DrawGestures {
  // `drawCtx` mirrors `strokeCtx`/`roomCtx`'s "latest closure via ref" discipline so the window-
  // level mouseup net and the finishers always read the current color/actor/dispatch.
  const drawCtx = useRef({ drawColor, actorId, dispatch });
  drawCtx.current = { drawColor, actorId, dispatch };

  const freehandRef = useRef<DrawPoint[] | null>(null);
  const [freehandPreview, setFreehandPreview] = useState<DrawPoint[] | null>(null);
  const finishFreehand = useCallback(() => {
    const points = freehandRef.current;
    freehandRef.current = null;
    setFreehandPreview(null);
    if (!points || points.length === 0) return;
    const { drawColor, actorId, dispatch } = drawCtx.current;
    const cmd = freehandAnnotationCommand({ actorId, color: drawColor, points });
    if (cmd) dispatch(cmd);
  }, []);

  const polylineRef = useRef<DrawPoint[] | null>(null);
  const [polylinePreview, setPolylinePreview] = useState<DrawPoint[] | null>(null);
  const [polylineCursor, setPolylineCursor] = useState<DrawPoint | null>(null);
  // The RAW (un-deduped) world point of the last two `mousedown`s while a polyline is accumulating
  // — kept ONLY to disambiguate `onStageDblClick`. The native `dblclick` DOM event fires purely
  // off the browser's own timing window with no distance check at all: two clicks on the same element
  // within that window fire `dblclick`, no matter how far apart they landed — and worse, a burst of 3+
  // rapid clicks (a GM placing vertices quickly, or this Phase's own e2e flow) fires `dblclick` after
  // EVERY click past the first, not just a genuine same-spot double-click. Without this guard, that
  // spurious event finishes the polyline after 2 vertices no matter how many more the user intended to
  // place. See `onStageDblClick`.
  const polylineRawClicksRef = useRef<[DrawPoint, DrawPoint] | null>(null);
  // Esc drops a half-drawn polyline (`decisions/0189` point 7): its vertices go, nothing is sent.
  const dropPolyline = useCallback(() => {
    polylineRef.current = null;
    polylineRawClicksRef.current = null;
    setPolylinePreview(null);
    setPolylineCursor(null);
  }, []);
  const finishPolyline = useCallback(() => {
    const points = polylineRef.current;
    dropPolyline();
    if (!points) return;
    const { drawColor, actorId, dispatch } = drawCtx.current;
    const cmd = polylineAnnotationCommand({ actorId, color: drawColor, points });
    if (cmd) dispatch(cmd);
  }, [dropPolyline]);

  // `eraserCtx` is the SAME "ref carries the latest closure" discipline `drawCtx` uses just above —
  // `isGm`/`actorId`/`annotations`/`dispatch` can all change between a press and a much-later release.
  const eraserCtx = useRef({ isGm, actorId, annotations, dispatch });
  eraserCtx.current = { isGm, actorId, annotations, dispatch };

  // Whether `id` is erasable by the CURRENT actor (D7) — reads the LIVE `annotations` off
  // `eraserCtx`, so an id that went stale (its stroke already removed by someone else, e.g. a
  // concurrent GM erase or the author's own Delete) between the mousemove that collected it and the
  // moment this is checked is silently excluded, never thrown on — the "build the batch defensively"
  // gotcha: the client-side optimistic apply runs the real reducer, and a malformed/unauthorized
  // batch throws inside the dispatch handler BEFORE the send, which would otherwise kill the WHOLE
  // gesture silently (no log on either end) over one stale id.
  const isErasable = useCallback((id: AnnotationId): boolean => {
    const { isGm, actorId, annotations } = eraserCtx.current;
    const a = annotations[id];
    return a !== undefined && (isGm || a.authorSlotId === actorId);
  }, []);

  const eraserRef = useRef<{ lastPoint: DrawPoint; ids: Set<AnnotationId> } | null>(null);
  const [erasingIds, setErasingIds] = useState<ReadonlySet<AnnotationId>>(NONE);
  // Collect every erasable stroke the segment `from`→`to` touches (D6), and hide the new ones.
  const collect = useCallback(
    (gesture: { lastPoint: DrawPoint; ids: Set<AnnotationId> }, to: DrawPoint) => {
      const before = gesture.ids.size;
      for (const id of annotationsAlongSegment(eraserCtx.current.annotations, { from: gesture.lastPoint, to })) {
        if (isErasable(id)) gesture.ids.add(id);
      }
      gesture.lastPoint = to;
      if (gesture.ids.size !== before) setErasingIds(new Set(gesture.ids));
    },
    [isErasable],
  );
  const openEraser = useCallback(
    (world: DrawPoint) => {
      // A zero-length first "travel" is a point test, so a click without a drag erases what's under it.
      const gesture = { lastPoint: world, ids: new Set<AnnotationId>() };
      eraserRef.current = gesture;
      collect(gesture, world);
    },
    [collect],
  );
  const sampleEraser = useCallback(
    (world: DrawPoint) => {
      if (eraserRef.current) collect(eraserRef.current, world);
    },
    [collect],
  );
  const dropEraser = useCallback(() => {
    eraserRef.current = null;
    setErasingIds(NONE);
  }, []);
  const finishEraser = useCallback(() => {
    const gesture = eraserRef.current;
    eraserRef.current = null;
    setErasingIds(NONE);
    if (!gesture || gesture.ids.size === 0) return; // zero hits → no command (never an empty batch)
    const { actorId, dispatch } = eraserCtx.current;
    // Re-filter at commit time too, not just when each id was collected during the drag — see
    // `isErasable`'s doc comment above.
    const ids = [...gesture.ids].filter(isErasable);
    const cmd = eraseAnnotationsCommand({ actorId, annotationIds: ids });
    if (cmd) dispatch(cmd);
  }, [isErasable]);

  return {
    drawCtx,
    freehandRef,
    freehandPreview,
    setFreehandPreview,
    finishFreehand,
    polylineRef,
    polylinePreview,
    setPolylinePreview,
    polylineCursor,
    setPolylineCursor,
    polylineRawClicksRef,
    finishPolyline,
    dropPolyline,
    eraserCtx,
    isErasable,
    eraserRef,
    openEraser,
    sampleEraser,
    finishEraser,
    dropEraser,
    erasingIds,
  };
}
